import { ApiError, API_BASE_URL, markReachable, markUnreachable, networkError } from './client.ts';
import type { RequestMode, Song } from './types.ts';

/**
 * Every endpoint SPEC §4.6 grants the Public role, for the guest request page (RequestPage.tsx and
 * friends). Deliberately its OWN small fetch helper (`guestFetch` below), never `api/client.ts`'s
 * `apiFetch` — `apiFetch` attaches `Authorization: Bearer <token>` to EVERY request whenever one is
 * stored (client.ts's `apiFetch`, unconditionally). That is harmless for the operator console,
 * which only ever runs in its own tab, but the guest page is reached by scanning a QR code, and
 * nothing stops a KJ from opening `/j/<code>` in the SAME browser — a second tab, or the same one
 * out of curiosity — that is also signed in to the operator console. If the guest page's requests
 * went through `apiFetch`, that tab's operator JWT would ride along on every guest search/request
 * call: a real credential leak on a page that must work identically for, and look identical to, a
 * total stranger's phone. This module never imports `getToken`/`setToken`/`onUnauthorized` and
 * never will; it only reuses `ApiError` (a plain data class), `API_BASE_URL` (a constant) and
 * `networkError` (a pure factory over `navigator.onLine`), none of which touch storage. MVP-483
 * adds `markReachable`/`markUnreachable` to that list — two setters over a module-scoped enum, with
 * no more access to a token than the three above.
 *
 * `api/catalogSearch.ts` (catalog-search's operator-facing equivalent) intentionally stays a
 * SEPARATE implementation for the exact same reason, even though both ultimately call
 * `GET /api/catalog/search` — see that module's own doc comment.
 */

// ---------------------------------------------------------------------------------------------
// guestFetch — deliberately not apiFetch (see module doc comment above)
// ---------------------------------------------------------------------------------------------

/** The shape every guest-facing request that carries a duplicate-request's existing row can
 * respond with (song-request controller's `respondError` + `ctx.body.data = ...` for 409). */
export class GuestApiError extends ApiError {
  /** Only ever set for a 409 `DUPLICATE_REQUEST` — the ALREADY-EXISTING request the server found
   * (song-request controller's `create()` deliberately echoes it: SPEC §4.2 "the original, never a
   * second row"). `RequestPage` treats this as a soft success rather than a failure to show. */
  data?: GuestRequestView;

  constructor(status: number, message: string, code?: string, details?: Record<string, unknown>, data?: GuestRequestView) {
    super(status, message, code, details);
    this.name = 'GuestApiError';
    this.data = data;
  }
}

interface GuestFetchOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

interface GuestErrorEnvelope {
  data?: unknown;
  error?: { status: number; name: string; message: string; details?: Record<string, unknown> };
}

async function guestFetch<T>(path: string, options: GuestFetchOptions = {}): Promise<T> {
  const url = `${API_BASE_URL}${path}`;
  const hasBody = options.body !== undefined;
  const headers = hasBody ? { 'Content-Type': 'application/json' } : undefined;

  let res: Response;
  try {
    res = await fetch(url, {
      method: options.method ?? 'GET',
      headers,
      body: hasBody ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });
  } catch (err) {
    // MVP-483: an abort is the CALLER stopping, not the network failing — the same guard
    // `doFetch()` carries, and it matters MORE here. The guest search box refires on every
    // keystroke and aborts the request in flight each time (`GuestSearchParams.signal`), so without
    // this branch a customer simply typing a song title would abort ~one request per character and
    // drive their OWN page to "offline" mid-search. Rethrown untouched, reachability left exactly
    // as the last real request found it.
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    // MVP-483: this is the guest route's only reachability signal — `guestFetch` deliberately never
    // goes through `doFetch` (module doc comment above), so before this line `getReachability()` was
    // permanently 'unknown' on `/j/<code>` and the page had nothing to veil itself with.
    markUnreachable();
    // MVP-390: was a hand-copied duplicate of client.ts's sentence, which is exactly how the two
    // drifted — this one never had R7-P2-4's offline/unreachable split and told every guest to
    // check a connection that was usually fine. Same function as `doFetch()` now (DESIGN.md §6.4's
    // "offline" pattern, naming the failing host).
    throw networkError(API_BASE_URL);
  }

  // Any actual HTTP response — even a 4xx/5xx — proves the server itself is reachable; only a
  // thrown `fetch()` (the `catch` above) means it genuinely was not. Same rule as `doFetch()`.
  markReachable();

  let body: GuestErrorEnvelope | undefined;
  try {
    body = (await res.json()) as GuestErrorEnvelope;
  } catch {
    /* no JSON body to parse (e.g. an upstream proxy error page) */
  }

  if (!res.ok) {
    const code = (body?.error?.details?.code as string | undefined) ?? body?.error?.name;
    throw new GuestApiError(
      res.status,
      body?.error?.message ?? `Request failed with status ${res.status}`,
      code,
      body?.error?.details,
      (body?.data as GuestRequestView | null | undefined) ?? undefined
    );
  }

  return body as T;
}

// ---------------------------------------------------------------------------------------------
// GET /api/sessions/join?code=  ·  POST /api/sessions/join (session controller's `join`)
// ---------------------------------------------------------------------------------------------

export interface SessionJoinInfo {
  venue: string | null;
  name: string | null;
  joinCode: string;
  requestMode: RequestMode;
  passcodeRequired: boolean;
  /** Only meaningful when `passcodeRequired` is true — never implies "correct" otherwise. */
  passcodeOk: boolean;
  message: string | null;
  quota: { maxPendingPerDevice: number; windowMax: number; windowMinutes: number };
  serverTimeMs: number;
  /** MVP-471. `required` is whether this deployment has Cloudflare Turnstile configured at all,
   * and `siteKey` is what renders the widget. Both come from the server rather than the web build,
   * so switching Turnstile on is a backend .env change and not a redeploy of this bundle. */
  turnstile: { required: boolean; siteKey: string | null };
  /** MVP-471. Non-null only on the POST that just passed a challenge (or re-presented a pass this
   * phone already held). Opaque here — the server signs and checks it; this page only stores it
   * and sends it back. See `lib/deviceId.ts`'s `getGuestPass`. */
  guestPass: string | null;
}

/** The one thing a guest's browser calls before it ever sees a song (SPEC §4.2).
 *
 * Two transports, one endpoint, and which one is used is decided by whether there is a passcode to
 * send. MVP-469: a passcode may never ride in a URL — it would be written verbatim into the
 * reverse proxy's access log, this browser's own history, and the `Referer` header of every
 * request the page makes afterwards, and it is a short PIN the KJ reads out loud to the room.
 *
 *   - no passcode -> GET. The cold load every scanned QR makes. The server sends
 *     `Cache-Control: public, max-age=5`, so calling it again (on `visibilitychange`, say) usually
 *     costs no round trip. A gated session comes back `passcodeOk: false`, which is what puts the
 *     gate on screen.
 *   - passcode     -> POST, `no-store`. Wrong answers are rate-limited per device and per address
 *     (429 `RATE_LIMITED`), so this is not a field to retry in a loop.
 *
 * `deviceId` is what buys this phone its own allowance instead of sharing the venue's single NAT
 * address with every other phone in the room — always pass it when one is available.
 */
export interface JoinOptions {
  passcode?: string;
  deviceId?: string;
  /** MVP-471: a token the Turnstile widget just produced. Sending one is what mints a guest pass. */
  turnstileToken?: string;
  /** MVP-471: the pass this phone already holds, if any. Presenting a valid one re-joins without a
   * second challenge — which is what keeps the passcode gate (which POSTs here on every attempt)
   * and a plain page reload from putting the widget back on screen. */
  guestPass?: string | null;
}

export async function joinSession(code: string, options: JoinOptions = {}): Promise<SessionJoinInfo> {
  const { passcode, deviceId, turnstileToken, guestPass } = options;

  // MVP-471 widened what the POST is for. It is no longer only "a passcode to check": it is the
  // verb that carries anything this phone must PROVE — the venue PIN, a solved challenge, or a
  // pass it already holds. The GET stays the cacheable cold load with nothing to prove.
  if (passcode || turnstileToken || guestPass) {
    const res = await guestFetch<{ data: SessionJoinInfo }>('/api/sessions/join', {
      method: 'POST',
      body: { code, passcode, deviceId, turnstileToken, guestPass },
    });
    return res.data;
  }

  const res = await guestFetch<{ data: SessionJoinInfo }>(`/api/sessions/join?${new URLSearchParams({ code }).toString()}`);
  return res.data;
}

// ---------------------------------------------------------------------------------------------
// GET /api/catalog/search — the guest's read (AC-REQ-3: same endpoint/index/ranking as the KJ)
// ---------------------------------------------------------------------------------------------

export interface GuestSearchParams {
  q: string;
  limit?: number;
  /** MVP-274 R1: the guest page's browse door. Only ever `'code'` — the catalogue's own numbering,
   * which is what the venue's printed song book is ordered by, so a customer with no book and no
   * title in mind can still scroll the same list the book prints. Ranked relevance (the default)
   * is meaningless without a query, and every other sort in `search.js`'s `SORTS` would order the
   * browse list by something the customer cannot see. */
  sort?: 'code';
  /** Keyset cursor from a previous call's `cursor`, for the browse list's "show more". Search
   * results are capped at `SEARCH_LIMIT` and never paginate — a guest who cannot find their song
   * in 30 ranked matches needs a different query, not page 2. */
  cursor?: string | null;
  signal?: AbortSignal;
}

export interface GuestSearchResult {
  songs: Song[];
  cursor: string | null;
  hasMore: boolean;
  total: number;
}

/**
 * Deliberately narrower than `api/catalogSearch.ts` (Task B's operator equivalent): no facets, no
 * filter, and only the one `sort` the guest page can actually offer. The guest page is find-and-tap,
 * not the operator's full faceted browse — see this file's own module doc comment for why the two
 * never merge into one implementation despite hitting the identical route.
 */
export async function guestSearch({ q, limit, sort, cursor, signal }: GuestSearchParams): Promise<GuestSearchResult> {
  const params = new URLSearchParams();
  const trimmed = q.trim();
  if (trimmed) params.set('q', trimmed);
  if (limit) params.set('limit', String(limit));
  if (sort) params.set('sort', sort);
  if (cursor) params.set('cursor', cursor);
  const qs = params.toString();
  const res = await guestFetch<{ data: Song[]; meta: { cursor: string | null; hasMore: boolean; total: number } }>(
    `/api/catalog/search${qs ? `?${qs}` : ''}`,
    { signal }
  );
  return { songs: res.data, cursor: res.meta.cursor, hasMore: res.meta.hasMore, total: res.meta.total };
}

// ---------------------------------------------------------------------------------------------
// POST /api/requests, GET /api/requests/mine — the guest's one write, and reading it back
// ---------------------------------------------------------------------------------------------

// MVP-11 #21 deviation (verified against strapi-template/src/api/song-request/services/song-request.js,
// not merely the schema): the wave-2 plan and cohesion-r1.md #21 both describe `played` as
// "client-only, the server never emits it," citing `song-request`'s schema enum
// (`pending|accepted|rejected|expired` — the RAW stored column, correct as far as it goes). But
// `mapForGuest()` (the function that actually builds every guest-facing response — POST
// /api/requests, GET /api/requests/mine, `request.mine` pushes, AND this exact 409's `.data`) does
// synthesize `status: 'played'` on the wire, at song-request.js:168, whenever an `accepted` row's
// `queueEntry.status === 'done'`. That is a real value real guest devices receive tonight, not a
// client invention — removing it would silently break `describeQueuePosition`'s "Played — thanks
// for singing!" copy and MVP-11 #33's duplicate-status handling below for every song that has
// already been sung. Kept for that reason; flagged as a plan/reality mismatch rather than executed
// as instructed — see this wave's report.
export type GuestRequestStatus = 'pending' | 'accepted' | 'played' | 'rejected' | 'expired';

/** song-request service's `mapForGuest()` shape exactly — never the operator-only tray fields
 * (device hash, block state, other guests' rows). `position`/`etaSec` are only present while
 * `status === 'accepted'` AND the queue entry is still actually queued/playing. */
export interface GuestRequestView {
  requestId: string;
  songId: string | null;
  songTitle: string | null;
  singerName: string;
  keyShift: number;
  createdAt: string;
  status: GuestRequestStatus;
  position?: number;
  etaSec?: number;
  /** R10-P2-3 fix: true only when THIS request's own queue-entry is the one currently playing —
   * `computePositionAndEta` alone reports `position: 1, etaSec: 0` for that case, wire-identical
   * to "about to go up next" (song-request service's `mapForGuest()`). Only ever present alongside
   * `status === 'accepted'`; absent (not merely false) for every other status. */
  nowPlaying?: boolean;
  rejectReason?: string | null;
}

export interface SubmitRequestParams {
  joinCode?: string;
  /** The song's `documentId` — the field is literally named `song` on the wire (song-request
   * schema.json's relation field), not `songId`. */
  song: string;
  singerName: string;
  deviceId: string;
  keyShift?: number;
  note?: string;
  passcode?: string;
  /** MVP-471: proof this phone met the challenge at the join gate. Required by the server only
   * when Turnstile is configured; a 403 `CHALLENGE_REQUIRED` means it is missing or stale, and the
   * page should drop it and re-challenge rather than retry. */
  guestPass?: string | null;
  /** `crypto.randomUUID()`, reused across retries of the SAME attempt (AC-REQ-9: "retry on
   * reconnect with idempotencyKey so duplicates are impossible") and minted fresh only for a new
   * one — see RequestPage.tsx's own comment for exactly when. */
  idempotencyKey: string;
}

/** POST /api/requests — public (SPEC §4.2). The one write a guest phone may ever make. Throws
 * `GuestApiError` on any failure; a 409 `DUPLICATE_REQUEST` carries the pre-existing request in
 * `.data` (see that class's own doc comment) rather than losing it. */
export async function submitRequest(params: SubmitRequestParams): Promise<GuestRequestView> {
  const res = await guestFetch<{ data: GuestRequestView }>('/api/requests', {
    method: 'POST',
    body: {
      song: params.song,
      singerName: params.singerName,
      deviceId: params.deviceId,
      joinCode: params.joinCode,
      keyShift: params.keyShift,
      note: params.note,
      passcode: params.passcode,
      guestPass: params.guestPass,
      idempotencyKey: params.idempotencyKey,
    },
  });
  return res.data;
}

/** GET /api/requests/mine?deviceId=[&joinCode=] — public (SPEC §4.2). Every one of this device's
 * requests, newest first, each mapped exactly like `submitRequest()`'s response so the client can
 * use one type for "just submitted" and "loaded on return to the tab" alike.
 *
 * MVP-622: pass `joinCode` and the server answers with THIS night's rows only — without it a
 * phone that was here last night gets last night's row back as its newest. Optional because this
 * bundle deploys independently of the backend; an older backend simply ignores the parameter. */
export async function myRequests(deviceId: string, joinCode?: string): Promise<GuestRequestView[]> {
  const params = new URLSearchParams({ deviceId });
  if (joinCode) params.set('joinCode', joinCode);
  const res = await guestFetch<{ data: GuestRequestView[] }>(`/api/requests/mine?${params.toString()}`);
  return res.data;
}
